import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { formatDate, formatRelativeOrDate } from '../utils/dateFormat';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Banner,
  Button,
  Modal,
  SkeletonPage,
  SkeletonBodyText,
  SkeletonDisplayText,
  Divider,
  Box,
  Icon,
  Link,
  Thumbnail,
  ProgressBar,
  Collapsible,
} from '@shopify/polaris';
import { ExternalIcon, ArrowLeftIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@shopify/polaris-icons';
import { useProductDetail, useScoreHistory, useRecheckReadiness } from '../hooks/useProductScore';
import { useStoreScore } from '../hooks/useStoreScore';
import { ScoreGauge } from '../components/ScoreGauge';
import { SubScoreBreakdown } from '../components/SubScoreBreakdown';
import { RescanButton } from '../components/RescanButton';
import { PageEntrance, StaggeredList } from '../components/ui';
import './ProductDetailPage.css';
import { AutoFixPreview } from '../components/AutoFixPreview';
import { ProductCategorySection } from '../components/ProductCategorySection';
import { ProductSummarySection } from '../components/ProductSummarySection';
import { useFixHistory, useRevertFix, useReapplyFix } from '../hooks/useAutoFix';
import { useSubscriptionStatus } from '../hooks/useSubscription';
import { useCategoryTemplates, useOverrideCategory } from '../hooks/useCategory';
import { getScoreTierConfig } from '../theme';
import type { Colour } from '../types';

/**
 * T050: ProductDetailPage.
 * Overall score card, SubScoreBreakdown, score history chart,
 * identifier badges, navigation back to product list.
 */

function barcodeStatusLabel(status: string): { label: string; tone: 'success' | 'warning' | 'critical' } {
  switch (status) {
    case 'VALID':
      return { label: 'GTIN Valid', tone: 'success' };
    case 'INVALID':
      return { label: 'GTIN Invalid', tone: 'critical' };
    default:
      return { label: 'GTIN Missing', tone: 'warning' };
  }
}

export function ProductDetailPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as { productIds: string[]; currentIndex: number } | null;
  const productIds = navState?.productIds ?? null;
  const currentIndex = navState?.currentIndex ?? null;
  const prevId = productIds && currentIndex !== null && currentIndex > 0 ? productIds[currentIndex - 1] : null;
  const nextId = productIds && currentIndex !== null && currentIndex < productIds.length - 1 ? productIds[currentIndex + 1] : null;
  const { data: product, isLoading, error } = useProductDetail(id);
  const { data: scoreHistory } = useScoreHistory(id);
  const { data: storeData } = useStoreScore();
  const { data: fixHistory } = useFixHistory(id ?? '');
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [ucpCriteriaOpen, setUcpCriteriaOpen] = useState(false);
  const [acpHowToFixOpen, setAcpHowToFixOpen] = useState(false);
  const [ucpHowToFixOpen, setUcpHowToFixOpen] = useState(false);
  const [scoreHistoryOpen, setScoreHistoryOpen] = useState(false);
  const recheckMutation = useRecheckReadiness(id);
  const {
    data: subscriptionStatus,
    isLoading: subscriptionStatusLoading,
  } = useSubscriptionStatus();
  const revertMutation = useRevertFix();
  const reapplyMutation = useReapplyFix();
  const hasCategoryTemplateAccess = subscriptionStatus?.features.categoryTemplates ?? false;
  const isAiReadinessLocked = subscriptionStatus?.tier === 'FREE';
  const { data: allTemplates = [] } = useCategoryTemplates(hasCategoryTemplateAccess);
  const overrideMutation = useOverrideCategory(id ?? '');

  if (isLoading) {
    return (
      <SkeletonPage title="Product Detail" backAction>
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonDisplayText size="small" />
              <SkeletonBodyText lines={4} />
            </Card>
          </Layout.Section>
        </Layout>
      </SkeletonPage>
    );
  }

  if (error || !product) {
    return (
      <Page
        title="Product Not Found"
        backAction={{ content: 'Products', onAction: () => navigate('/products') }}
      >
        <Banner tone="critical">
          <p>{error?.message ?? 'Unable to load product details.'}</p>
        </Banner>
      </Page>
    );
  }

  const barcode = barcodeStatusLabel(product.barcodeStatus);
  const tierConfig = getScoreTierConfig(product.overallScore);

  return (
    <PageEntrance>
    <Page>
      <BlockStack gap="400">
        {/* Custom page header: back arrow + image + title on one line */}
        <div className="detail-page-header">
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <Button
              icon={ArrowLeftIcon}
              variant="plain"
              accessibilityLabel="Back to Products"
              onClick={() => navigate('/products')}
            />
            <Thumbnail
              source={product.imageUrl ?? 'placeholder'}
              alt=""
              size="small"
            />
            <BlockStack gap="050">
              <Text as="h1" variant="headingXl">{product.title}</Text>
              {product.vendor && (
                <Text as="p" variant="bodySm" tone="subdued">by {product.vendor}</Text>
              )}
            </BlockStack>
            <Badge tone={tierConfig.badgeTone}>
              {tierConfig.label}
            </Badge>
          </InlineStack>
          {(prevId || nextId) && (
            <InlineStack gap="200" blockAlign="center" align="space-between">
              <InlineStack gap="200" blockAlign="center">
                <Button
                  variant="secondary"
                  disabled={!prevId}
                  onClick={() => prevId && navigate(`/products/${prevId}`, { state: { productIds: productIds!, currentIndex: currentIndex! - 1 } })}
                  accessibilityLabel="Previous product"
                >
                  ← Prev
                </Button>
                <Text as="span" variant="bodySm" tone="subdued">
                  {currentIndex! + 1} / {productIds!.length}
                </Text>
              </InlineStack>
              <Button
                variant="secondary"
                disabled={!nextId}
                onClick={() => nextId && navigate(`/products/${nextId}`, { state: { productIds: productIds!, currentIndex: currentIndex! + 1 } })}
                accessibilityLabel="Next product"
              >
                Next →
              </Button>
            </InlineStack>
          )}
        </div>

      <Layout>
        {/* Overall Score Card */}
        <Layout.Section variant="oneThird">
          <StaggeredList>
          <Card>
            <BlockStack gap="400" inlineAlign="center">
              <ScoreGauge
                score={product.overallScore}
                colour={product.colour as Colour}
                size="large"
              />
              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                Algorithm v{product.algoVersion}
                {product.lastScoredAt && (
                  <>
                    {' · '}
                    Last scored {formatRelativeOrDate(product.lastScoredAt)}
                  </>
                )}
              </Text>
              <RescanButton target={product.id} tier={storeData?.store.tier} />
              {product.storefrontUrl && (
                <Link url={product.storefrontUrl} target="_blank">
                  <InlineStack gap="100" blockAlign="center">
                    <Text as="span" variant="bodySm">View on Store</Text>
                    <Icon source={ExternalIcon} tone="interactive" />
                  </InlineStack>
                </Link>
              )}
              {storeData?.store.shopDomain && product.shopifyProductId && (
                <Link
                  url={`https://admin.shopify.com/store/${storeData.store.shopDomain.replace('.myshopify.com', '')}/products/${product.shopifyProductId}`}
                  target="_blank"
                >
                  <InlineStack gap="100" blockAlign="center">
                    <Text as="span" variant="bodySm">Edit in Shopify</Text>
                    <Icon source={ExternalIcon} tone="interactive" />
                  </InlineStack>
                </Link>
              )}
            </BlockStack>
          </Card>

          {/* Identifier Badges */}
          <Box paddingBlockStart="400">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Identifiers
                </Text>
                <InlineStack gap="200" wrap>
                  <Badge tone={barcode.tone}>{barcode.label}</Badge>
                  <Badge tone={product.hasSku ? 'success' : 'warning'}>
                    {product.hasSku ? 'SKU Present' : 'No SKU'}
                  </Badge>
                  {product.hasSku && (
                    <Badge tone={product.skuUnique ? 'success' : 'critical'}>
                      {product.skuUnique ? 'SKU Unique' : 'Duplicate SKU'}
                    </Badge>
                  )}
                  <Badge tone={product.hasMpn ? 'success' : 'warning'}>
                    {product.hasMpn ? `MPN (${product.mpnSource ?? 'metafield'})` : 'No MPN'}
                  </Badge>
                </InlineStack>
              </BlockStack>
            </Card>
          </Box>

          {/* Quick Actions — navigate to suggestions, compliance, and simulation */}
          {/* Hidden for now — will be re-enabled in future */}
          {false && (
          <Box paddingBlockStart="400">
            <Card>
              <BlockStack gap="0">
                <Text as="h2" variant="headingMd">Actions</Text>
                <Box paddingBlockStart="400">
                  <BlockStack gap="0">

                    {/* Suggestions */}
                    <Box paddingBlockEnd="400">
                      <BlockStack gap="100">
                          <Text as="p" variant="headingSm">💡 Fix Suggestions</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          AI-powered recommendations to boost your product's discoverability score.
                        </Text>
                        <InlineStack align="end">
                          <Button variant="secondary" onClick={() => navigate(`/products/${id}/suggestions`)}>
                            View suggestions
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </Box>

                    <Divider />

                    {/* Compliance Check */}
                    <Box paddingBlockStart="400" paddingBlockEnd="400">
                      <BlockStack gap="100">
                        <Text as="p" variant="headingSm">✅ Compliance Check</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          Verify your product meets structured data and product feed requirements.
                        </Text>
                        <InlineStack align="end">
                          <Button variant="secondary" onClick={() => navigate(`/products/${id}/compliance`)}>
                            View compliance
                          </Button>
                        </InlineStack>
                      </BlockStack>
                    </Box>

                    <Divider />

                    {/* AI Crawl Simulation */}
                    <Box paddingBlockStart="400">
                      <BlockStack gap="100">
                        <Text as="p" variant="headingSm">🔬 AI Crawl Simulation</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          See exactly what ChatGPT, Perplexity, and AI shopping assistants extract from your product page.
                        </Text>
                        {storeData?.store.tier !== 'SCALE' && (
                          <Text as="p" variant="bodySm" tone="subdued">
                            🔒 Available on the Scale plan. Upgrade to unlock.
                          </Text>
                        )}
                        <InlineStack align="end">
                          {storeData?.store.tier === 'SCALE' ? (
                            <Button variant="secondary" onClick={() => navigate(`/products/${id}/simulation`)}>
                              Run simulation
                            </Button>
                          ) : (
                            <Button variant="secondary" disabled>
                              Run simulation
                            </Button>
                          )}
                        </InlineStack>
                      </BlockStack>
                    </Box>

                  </BlockStack>
                </Box>
              </BlockStack>
            </Card>
          </Box>
          )}

          {/* T078: Category Template */}
          {!subscriptionStatusLoading && (
            <ProductCategorySection
              hasAccess={hasCategoryTemplateAccess}
              detectedCategory={product.detectedCategory ?? null}
              confidence={(product.categoryConfidence as import('../types').CategoryConfidence) ?? null}
              categoryOverride={product.categoryOverride ?? null}
              template={null}
              allTemplates={allTemplates}
              presentAttributes={product.presentAttributes ?? []}
              attributeValues={product.attributeValues ?? {}}
              attributeValueSources={product.attributeValueSources ?? {}}
              onOverride={(slug) => overrideMutation.mutate(slug)}
              productId={product.id}
            />
          )}
          </StaggeredList>
        </Layout.Section>

        {/* Sub-Score Breakdown */}
        <Layout.Section>
          <SubScoreBreakdown
            subScores={product.subScores}
            globalCaps={product.globalCaps}
            isPaidTier={subscriptionStatus?.tier !== undefined && subscriptionStatus.tier !== 'FREE'}
            onFixSchemaQuality={() => navigate('/feed-settings')}
          />

          {/* AI Platform Readiness */}
          {(() => {
            const lockedAcpBreakdown = {
              hasVendor: true, hasDescription: true, hasLongDescription: false,
              hasSku: true, hasImage: true, hasAnyVariantAvailable: true,
              hasBarcode: false, hasMpn: false,
              hasProductType: true, hasVariantOptions: false, hasEnoughTags: true,
              hasMultipleImages: true, hasAltText: false, hasCompareAtPrice: false, hasQAndA: false,
            };
            const lockedUcpBreakdown = {
              hasDescription: true, hasImage: true, hasAnyVariantAvailable: true, hasVariantOptions: false,
              hasLongDescription: false, hasEnoughTags: true,
              hasRating: false, hasQAndA: false, hasCompareAtPrice: false,
            };
            const vAcpScore: number | null = isAiReadinessLocked ? 62 : product.acpScore;
            const vUcpScore: number | null = isAiReadinessLocked ? 55 : product.ucpScore;
            const vAcpEligibleSearch: boolean = isAiReadinessLocked ? false : product.acpEligibleSearch;
            const vAcpBreakdown = isAiReadinessLocked ? lockedAcpBreakdown : product.acpBreakdown;
            const vUcpBreakdown = isAiReadinessLocked ? lockedUcpBreakdown : product.ucpBreakdown;
            return (
          <Box paddingBlockStart="400">
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h2" variant="headingMd">AI Platform Readiness</Text>
                  {!isAiReadinessLocked && (
                    <Button
                      variant="plain"
                      size="slim"
                      loading={recheckMutation.isPending}
                      onClick={() => recheckMutation.mutate()}
                    >
                      Recheck
                    </Button>
                  )}
                </InlineStack>
                {isAiReadinessLocked && (
                  <Banner tone="info" title="Upgrade to unlock AI Platform Readiness">
                    <p>
                      ChatGPT and Gemini per-product scores are available on paid plans.
                      The values shown below are a preview. <Link url="/billing">Upgrade your plan</Link> to start scoring your products.
                    </p>
                  </Banner>
                )}
                <div className={isAiReadinessLocked ? 'product-detail-ai-readiness-locked' : undefined}>
                  <BlockStack gap="400">
                <BlockStack gap="300">
                  <BlockStack gap="100">
                    <InlineStack align="space-between">
                      <BlockStack gap="050">
                        <Text as="h3" variant="headingSm">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
                              <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.677l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0L4.001 14.614A4.5 4.5 0 0 1 2.34 7.896zm16.597 3.855-5.843-3.387L15.115 7.2a.076.076 0 0 1 .071 0l4.816 2.786a4.494 4.494 0 0 1-.676 8.105V12.48a.795.795 0 0 0-.39-.73zm2.01-3.023-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.08.08 0 0 1 .032-.062l4.808-2.782a4.496 4.496 0 0 1 6.67 4.66zm-12.64 4.135-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.496 4.496 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365 2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z"/>
                            </svg>
                            ChatGPT
                          </span>
                        </Text>
                        {vAcpScore !== null && (
                          <InlineStack gap="100" blockAlign="center">
                            <Text as="span" variant="bodySm" tone="subdued">
                              {vAcpScore >= 80 ? 'Discoverable'
                                : vAcpScore >= 60 ? 'Readable'
                                : vAcpScore >= 40 ? 'Confusing'
                                : 'Invisible'}
                              {' ·'}
                            </Text>
                            <InlineStack gap="100" blockAlign="center">
                              <Icon
                                source={vAcpEligibleSearch ? CheckCircleIcon : XCircleIcon}
                                tone={vAcpEligibleSearch ? 'success' : 'critical'}
                              />
                              <Text
                                as="span"
                                variant="bodySm"
                                tone={vAcpEligibleSearch ? 'success' : 'critical'}
                              >
                                {vAcpEligibleSearch ? 'Search eligible' : 'Not search eligible'}
                              </Text>
                            </InlineStack>
                          </InlineStack>
                        )}
                      </BlockStack>
                      <Text
                        as="span"
                        variant="bodySm"
                        fontWeight="semibold"
                        tone={vAcpScore !== null && vAcpScore < 40 ? 'critical' : undefined}
                      >
                        {vAcpScore !== null ? Math.round(vAcpScore) : '—'}
                      </Text>
                    </InlineStack>
                    <ProgressBar
                      progress={vAcpScore ?? 0}
                      size="small"
                      tone={
                        vAcpScore === null ? 'highlight'
                        : vAcpScore >= 60 ? 'success'
                        : vAcpScore >= 40 ? 'highlight'
                        : 'critical'
                      }
                    />
                    {!vAcpEligibleSearch && !isAiReadinessLocked && (() => {
                      const missing = [
                        !product.vendor        && 'Vendor',
                        !product.description   && 'Description',
                        product.imageCount < 1 && 'Image',
                        !product.hasSku        && 'SKU',
                      ].filter(Boolean) as string[];
                      return missing.length > 0 ? (
                        <Text as="p" variant="bodySm" tone="critical">
                          Missing: {missing.join(' · ')}
                        </Text>
                      ) : null;
                    })()}
                  </BlockStack>

                  {/* ACP criteria breakdown */}
                  {vAcpBreakdown !== null && vAcpScore !== 100 && (
                    <>
                      <Divider />
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="p" variant="bodySm" tone="subdued" fontWeight="medium">
                            Score criteria
                          </Text>
                          <Button
                            variant="plain"
                            size="slim"
                            icon={criteriaOpen ? ChevronUpIcon : ChevronDownIcon}
                            onClick={() => setCriteriaOpen((v) => !v)}
                            accessibilityLabel={criteriaOpen ? 'Hide score criteria' : 'Show score criteria'}
                            disabled={isAiReadinessLocked}
                          >
                            {criteriaOpen ? 'Hide' : 'Show'}
                          </Button>
                        </InlineStack>

                        <Collapsible
                          open={criteriaOpen}
                          id="acp-criteria-collapsible"
                          transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                          <BlockStack gap="200">
                        {/* Group A — Feed Required Fields */}
                        <Text as="p" variant="bodySm" tone="subdued" fontWeight="bold">Feed Required Fields</Text>
                        {(vAcpScore === null ? [] : [
                          { key: 'hasVendor',               label: 'Brand / Vendor',         pass: vAcpBreakdown.hasVendor },
                          { key: 'hasDescription',           label: 'Description',            pass: vAcpBreakdown.hasDescription },
                          { key: 'hasLongDescription',       label: 'Description ≥ 150 char', pass: vAcpBreakdown.hasLongDescription },
                          { key: 'hasSku',                   label: 'SKU',                    pass: vAcpBreakdown.hasSku },
                          { key: 'hasImage',                 label: 'Product image',          pass: vAcpBreakdown.hasImage },
                          { key: 'hasAnyVariantAvailable',   label: 'In stock / available',   pass: vAcpBreakdown.hasAnyVariantAvailable },
                        ]).map(({ key, label, pass }) => (
                          <div key={key} className="detail-acp-criterion">
                            <InlineStack align="space-between">
                              <Text as="span" variant="bodySm" tone={pass ? undefined : 'subdued'}>{label}</Text>
                              <Text as="span" variant="bodySm" tone={pass ? 'success' : 'critical'} fontWeight="medium">{pass ? '✓' : '✗'}</Text>
                            </InlineStack>
                          </div>
                        ))}

                        {/* Group B — Discovery Identifiers */}
                        <Box paddingBlockStart="100">
                          <Text as="p" variant="bodySm" tone="subdued" fontWeight="bold">Discovery Identifiers</Text>
                        </Box>
                        {(vAcpScore === null ? [] : [
                          { key: 'hasBarcode', label: 'GTIN / Barcode', pass: vAcpBreakdown.hasBarcode },
                          { key: 'hasMpn',     label: 'MPN',            pass: vAcpBreakdown.hasMpn },
                        ]).map(({ key, label, pass }) => (
                          <div key={key} className="detail-acp-criterion">
                            <InlineStack align="space-between">
                              <Text as="span" variant="bodySm" tone={pass ? undefined : 'subdued'}>{label}</Text>
                              <Text as="span" variant="bodySm" tone={pass ? 'success' : 'critical'} fontWeight="medium">{pass ? '✓' : '✗'}</Text>
                            </InlineStack>
                          </div>
                        ))}

                        {/* Group C — Structured Product Data */}
                        <Box paddingBlockStart="100">
                          <Text as="p" variant="bodySm" tone="subdued" fontWeight="bold">Structured Product Data</Text>
                        </Box>
                        {(vAcpScore === null ? [] : [
                          { key: 'hasProductType',    label: 'Product type',   pass: vAcpBreakdown.hasProductType },
                          { key: 'hasVariantOptions', label: 'Variant options', pass: vAcpBreakdown.hasVariantOptions },
                          { key: 'hasEnoughTags',     label: '3+ tags',         pass: vAcpBreakdown.hasEnoughTags },
                        ]).map(({ key, label, pass }) => (
                          <div key={key} className="detail-acp-criterion">
                            <InlineStack align="space-between">
                              <Text as="span" variant="bodySm" tone={pass ? undefined : 'subdued'}>{label}</Text>
                              <Text as="span" variant="bodySm" tone={pass ? 'success' : 'critical'} fontWeight="medium">{pass ? '✓' : '✗'}</Text>
                            </InlineStack>
                          </div>
                        ))}

                        {/* Group D — Media Richness */}
                        <Box paddingBlockStart="100">
                          <Text as="p" variant="bodySm" tone="subdued" fontWeight="bold">Media Richness</Text>
                        </Box>
                        {(vAcpScore === null ? [] : [
                          { key: 'hasMultipleImages', label: '2+ images',   pass: vAcpBreakdown.hasMultipleImages },
                          { key: 'hasAltText',        label: 'Alt text',    pass: vAcpBreakdown.hasAltText },
                          { key: 'hasCompareAtPrice', label: 'Sale price',  pass: vAcpBreakdown.hasCompareAtPrice },
                          { key: 'hasQAndA',          label: 'Q&A / FAQ',   pass: vAcpBreakdown.hasQAndA },
                        ]).map(({ key, label, pass }) => (
                          <div key={key} className="detail-acp-criterion">
                            <InlineStack align="space-between">
                              <Text as="span" variant="bodySm" tone={pass ? undefined : 'subdued'}>{label}</Text>
                              <Text as="span" variant="bodySm" tone={pass ? 'success' : 'critical'} fontWeight="medium">{pass ? '✓' : '✗'}</Text>
                            </InlineStack>
                          </div>
                        ))}
                            <InlineStack align="end">
                              <Button variant="plain" size="slim" onClick={() => setAcpHowToFixOpen(true)}>
                                How to fix?
                              </Button>
                            </InlineStack>
                          </BlockStack>
                        </Collapsible>
                      </BlockStack>
                    </>
                  )}
                </BlockStack>

                {/* ── Gemini Readiness (UCP) ── */}
                <Divider />
                <BlockStack gap="300">
                  <BlockStack gap="100">
                    <InlineStack align="space-between">
                      <BlockStack gap="050">
                        <Text as="h3" variant="headingSm">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            <svg width="16" height="16" viewBox="0 0 192 192" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                              <path d="M96 0C96 52.896 52.896 96 0 96c52.896 0 96 43.104 96 96 0-52.896 43.104-96 96-96-52.896 0-96-43.104-96-96Z" fill="url(#gemini-grad)"/>
                              <defs>
                                <linearGradient id="gemini-grad" x1="0" y1="0" x2="192" y2="192" gradientUnits="userSpaceOnUse">
                                  <stop stopColor="#4285F4"/>
                                  <stop offset="1" stopColor="#34A853"/>
                                </linearGradient>
                              </defs>
                            </svg>
                            Gemini
                          </span>
                        </Text>
                        {vUcpScore !== null && (
                          <Text as="span" variant="bodySm" tone="subdued">
                            {vUcpScore >= 80 ? 'Gemini Discoverable'
                              : vUcpScore >= 60 ? 'Gemini Readable'
                              : vUcpScore >= 40 ? 'Gemini Confusing'
                              : 'Gemini Invisible'}
                          </Text>
                        )}
                      </BlockStack>
                      <Text
                        as="span"
                        variant="bodySm"
                        fontWeight="semibold"
                        tone={vUcpScore !== null && vUcpScore < 40 ? 'critical' : undefined}
                      >
                        {vUcpScore !== null ? Math.round(vUcpScore) : '—'}
                      </Text>
                    </InlineStack>
                    <ProgressBar
                      progress={vUcpScore ?? 0}
                      size="small"
                      tone={
                        vUcpScore === null ? 'highlight'
                        : vUcpScore >= 60 ? 'success'
                        : vUcpScore >= 40 ? 'highlight'
                        : 'critical'
                      }
                    />
                    {vUcpScore !== null && vUcpScore < 40 && !isAiReadinessLocked && (
                      <Box
                        background="bg-surface-critical"
                        padding="200"
                        borderRadius="200"
                      >
                        <Text as="p" variant="bodySm" tone="critical">
                          CAP-5: Overall score capped at 70 because UCP Readiness score is below 40 (product is Gemini Invisible).
                        </Text>
                      </Box>
                    )}
                  </BlockStack>

                  {/* UCP criteria breakdown — collapsible */}
                  {vUcpBreakdown !== null && vUcpScore !== 100 && (
                    <>
                      <Divider />
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="center">
                          <Text as="p" variant="bodySm" tone="subdued" fontWeight="medium">
                            Score criteria
                          </Text>
                          <Button
                            variant="plain"
                            size="slim"
                            icon={ucpCriteriaOpen ? ChevronUpIcon : ChevronDownIcon}
                            onClick={() => setUcpCriteriaOpen((v) => !v)}
                            accessibilityLabel={ucpCriteriaOpen ? 'Hide Gemini criteria' : 'Show Gemini criteria'}
                            disabled={isAiReadinessLocked}
                          >
                            {ucpCriteriaOpen ? 'Hide' : 'Show'}
                          </Button>
                        </InlineStack>

                        <Collapsible
                          open={ucpCriteriaOpen}
                          id="ucp-criteria-collapsible"
                          transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                        >
                          <BlockStack gap="200">
                            {/* Group A — Protocol Required Fields & Availability */}
                            <Text as="p" variant="bodySm" tone="subdued" fontWeight="bold">Protocol Required Fields</Text>
                            {(vUcpScore === null ? [] : [
                              { key: 'hasDescription',         label: 'Description',          pass: vUcpBreakdown.hasDescription },
                              { key: 'hasImage',               label: 'Product image',        pass: vUcpBreakdown.hasImage },
                              { key: 'hasAnyVariantAvailable', label: 'In stock / available', pass: vUcpBreakdown.hasAnyVariantAvailable },
                              { key: 'hasVariantOptions',      label: 'Variant options',      pass: vUcpBreakdown.hasVariantOptions },
                            ]).map(({ key, label, pass }) => (
                              <div key={key} className="detail-acp-criterion">
                                <InlineStack align="space-between">
                                  <Text as="span" variant="bodySm" tone={pass ? undefined : 'subdued'}>{label}</Text>
                                  <Text as="span" variant="bodySm" tone={pass ? 'success' : 'critical'} fontWeight="medium">{pass ? '✓' : '✗'}</Text>
                                </InlineStack>
                              </div>
                            ))}

                            {/* Group B — Intent & Context Match */}
                            <Box paddingBlockStart="100">
                              <Text as="p" variant="bodySm" tone="subdued" fontWeight="bold">Intent &amp; Context Match</Text>
                            </Box>
                            {(vUcpScore === null ? [] : [
                              { key: 'hasLongDescription', label: 'Description ≥ 200 chars', pass: vUcpBreakdown.hasLongDescription },
                              { key: 'hasEnoughTags',      label: '3+ tags',                pass: vUcpBreakdown.hasEnoughTags },
                            ]).map(({ key, label, pass }) => (
                              <div key={key} className="detail-acp-criterion">
                                <InlineStack align="space-between">
                                  <Text as="span" variant="bodySm" tone={pass ? undefined : 'subdued'}>{label}</Text>
                                  <Text as="span" variant="bodySm" tone={pass ? 'success' : 'critical'} fontWeight="medium">{pass ? '✓' : '✗'}</Text>
                                </InlineStack>
                              </div>
                            ))}

                            {/* Group C — Conversational Trust & Value Signals */}
                            <Box paddingBlockStart="100">
                              <Text as="p" variant="bodySm" tone="subdued" fontWeight="bold">Trust &amp; Value Signals</Text>
                            </Box>
                            {(vUcpScore === null ? [] : [
                              { key: 'hasRating',         label: 'Reviews / ratings',    pass: vUcpBreakdown.hasRating },
                              { key: 'hasQAndA',          label: 'Q&A / FAQ',            pass: vUcpBreakdown.hasQAndA },
                              { key: 'hasCompareAtPrice', label: 'Sale / compare price', pass: vUcpBreakdown.hasCompareAtPrice },
                            ]).map(({ key, label, pass }) => (
                              <div key={key} className="detail-acp-criterion">
                                <InlineStack align="space-between">
                                  <Text as="span" variant="bodySm" tone={pass ? undefined : 'subdued'}>{label}</Text>
                                  <Text as="span" variant="bodySm" tone={pass ? 'success' : 'critical'} fontWeight="medium">{pass ? '✓' : '✗'}</Text>
                                </InlineStack>
                              </div>
                            ))}
                                <InlineStack align="end">
                                  <Button variant="plain" size="slim" onClick={() => setUcpHowToFixOpen(true)}>
                                    How to fix?
                                  </Button>
                                </InlineStack>
                          </BlockStack>
                        </Collapsible>
                      </BlockStack>
                    </>
                  )}
                </BlockStack>
                  </BlockStack>
                </div>
              </BlockStack>
            </Card>
          </Box>
            );
          })()}

          {/* Product Details */}
          <Box paddingBlockStart="400">
            <Card>
              <BlockStack gap="300">
                <Text as="h2" variant="headingMd">
                  Product Details
                </Text>
                <Divider />
                <InlineStack align="space-between">
                  <Text as="span" variant="bodySm" tone="subdued">Product Type</Text>
                  <Text as="span" variant="bodySm">{product.productType ?? '—'}</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" variant="bodySm" tone="subdued">Images</Text>
                  <Text as="span" variant="bodySm">{product.imageCount} ({product.hasAltText ? 'Alt text ✓' : 'No alt text'})</Text>
                </InlineStack>
                <InlineStack align="space-between">
                  <Text as="span" variant="bodySm" tone="subdued">Tags</Text>
                  <Text as="span" variant="bodySm">{product.tags.length > 0 ? product.tags.join(', ') : '—'}</Text>
                </InlineStack>
                {product.description && (
                  <>
                    <Divider />
                    <Text as="p" variant="bodySm" tone="subdued">
                      Description ({product.description.length} chars)
                    </Text>
                    <Text as="p" variant="bodySm">
                      {product.description.substring(0, 200)}
                      {product.description.length > 200 ? '…' : ''}
                    </Text>
                  </>
                )}
              </BlockStack>
            </Card>
          </Box>

          {/* T066: Auto-Fix Section */}
          {id && (
            <Box paddingBlockStart="400">
              <AutoFixPreview
                productId={id}
                currentScore={product.overallScore}
              />
            </Box>
          )}

          {/* T090: AI Summary Section */}
          {id && <ProductSummarySection productId={id} />}

          {/* T066: Undo History */}
          {fixHistory && fixHistory.length > 0 && (
            <Box paddingBlockStart="400">
              <Card>
                <BlockStack gap="300">
                  <Text as="h2" variant="headingMd">Fix History</Text>
                  {fixHistory.map((entry) => (
                    <InlineStack key={entry.id} align="space-between" blockAlign="start" wrap={false}>
                      <div style={{ flex: '1 1 0', minWidth: 0, overflow: 'hidden', marginInlineEnd: '12px' }}>
                        <BlockStack gap="050">
                          <Text as="span" variant="bodySm" fontWeight="semibold">
                            {entry.field}
                          </Text>
                          {entry.appliedValue && (
                            <Box paddingInlineStart="200">
                              <Text as="p" variant="bodyXs" tone="disabled">
                                <Text as="span" variant="bodyXs" tone="subdued">Original value: </Text>
                                {entry.appliedValue.length > 120
                                  ? `${entry.appliedValue.slice(0, 120)}…`
                                  : entry.appliedValue}
                              </Text>
                            </Box>
                          )}
                        </BlockStack>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        <BlockStack gap="100" inlineAlign="end">
                      {entry.revertedAt ? (
                        new Date(entry.expiresAt) > new Date() ? (
                          <InlineStack gap="200" blockAlign="center">
                            <Badge tone="info">Reverted</Badge>
                            <Button
                              size="slim"
                              onClick={() => reapplyMutation.mutate({
                                historyId: entry.id,
                                productId: id!,
                              })}
                              loading={reapplyMutation.isPending}
                            >
                              Re-apply
                            </Button>
                          </InlineStack>
                        ) : (
                          <Badge>Expired</Badge>
                        )
                      ) : new Date(entry.expiresAt) > new Date() ? (
                        <Button
                          size="slim"
                          onClick={() => revertMutation.mutate(
                            { historyId: entry.id, productId: id! },
                            {
                              onError: (err: any) => {
                                const isConflict = err.code === 'CONFLICT'
                                  || err.status === 409
                                  || err.message?.includes('modified since fix');
                                if (isConflict) {
                                  const shouldForce = globalThis.confirm(
                                    'This product field was modified after the fix was applied. Force revert to the original value anyway?',
                                  );
                                  if (shouldForce) {
                                    revertMutation.mutate({
                                      historyId: entry.id,
                                      productId: id!,
                                      force: true,
                                    });
                                  }
                                }
                              },
                            },
                          )}
                          loading={revertMutation.isPending}
                        >
                          Revert to Original
                        </Button>
                      ) : (
                        <Badge>Expired</Badge>
                      )}
                          <Text as="span" variant="bodyXs" tone="subdued">
                            Applied {formatRelativeOrDate(entry.appliedAt)}
                          </Text>
                        </BlockStack>
                      </div>
                    </InlineStack>
                  ))}
                </BlockStack>
              </Card>
            </Box>
          )}

          {/* Score History */}
          {scoreHistory && scoreHistory.length > 0 && (
            <Box paddingBlockStart="400">
              <Card>
                <BlockStack gap="300">
                  <InlineStack align="space-between" blockAlign="center">
                    <Text as="h2" variant="headingMd">
                      Score History
                    </Text>
                    <Button
                      variant="plain"
                      size="slim"
                      icon={scoreHistoryOpen ? ChevronUpIcon : ChevronDownIcon}
                      onClick={() => setScoreHistoryOpen((v) => !v)}
                      accessibilityLabel={scoreHistoryOpen ? 'Hide score history' : 'Show score history'}
                    >
                      {scoreHistoryOpen ? 'Hide' : 'Show'}
                    </Button>
                  </InlineStack>
                  <Collapsible
                    open={scoreHistoryOpen}
                    id="score-history-collapsible"
                    transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
                  >
                    <BlockStack gap="200">
                      {scoreHistory.map((entry) => (
                        <InlineStack key={entry.id} align="space-between">
                          <Text as="span" variant="bodySm">
                            {formatDate(entry.date)}
                          </Text>
                          <InlineStack gap="200">
                            <Text as="span" variant="bodySm" fontWeight="semibold">
                              {Math.round(entry.overallScore)}
                            </Text>
                            <Text as="span" variant="bodySm" tone="subdued">
                              v{entry.algoVersion}
                            </Text>
                          </InlineStack>
                        </InlineStack>
                      ))}
                    </BlockStack>
                  </Collapsible>
                </BlockStack>
              </Card>
            </Box>
          )}
        </Layout.Section>
      </Layout>
      </BlockStack>

      {/* ── ChatGPT Score — How to fix modal ── */}
      <Modal
        open={acpHowToFixOpen}
        onClose={() => setAcpHowToFixOpen(false)}
        title="How to fix your ChatGPT Score"
      >
        <Modal.Section>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm" fontWeight="bold">Feed Required Fields</Text>
              {([
                { label: 'Brand / Vendor', desc: 'In Shopify Admin, go to Products → select this product → scroll to "Organization" → fill in the Vendor field.' },
                { label: 'Description', desc: 'Add a product description in the product editor. Any text (even brief) is required to pass this signal.' },
                { label: 'Description ≥ 150 chars', desc: 'Your description must be at least 150 characters. Expand it with product benefits, specifications, or use-case details.' },
                { label: 'SKU', desc: 'Add a SKU to every variant. In the product editor, scroll to Variants → click each variant → fill in the SKU field.' },
                { label: 'Product image', desc: 'Upload at least one image for this product using the media section in the product editor.' },
                { label: 'In stock / available', desc: 'Ensure at least one variant is available. Check inventory settings — set inventory levels above zero or enable "Continue selling when out of stock".' },
              ] as { label: string; desc: string }[]).map(({ label, desc }) => (
                <BlockStack key={label} gap="050">
                  <Text as="span" variant="bodySm" fontWeight="semibold">{label}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{desc}</Text>
                </BlockStack>
              ))}
            </BlockStack>
            <Divider />
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm" fontWeight="bold">Discovery Identifiers</Text>
              {([
                { label: 'GTIN / Barcode', desc: 'Add a GTIN, UPC, EAN, or ISBN to your product variants. In the product editor → Variants → Barcode field. GTINs significantly improve ChatGPT shopping eligibility.' },
                { label: 'MPN', desc: 'Add a Manufacturer Part Number as a product metafield. Create a metafield with namespace "custom" and key "mpn" containing the manufacturer\'s part number.' },
              ] as { label: string; desc: string }[]).map(({ label, desc }) => (
                <BlockStack key={label} gap="050">
                  <Text as="span" variant="bodySm" fontWeight="semibold">{label}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{desc}</Text>
                </BlockStack>
              ))}
            </BlockStack>
            <Divider />
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm" fontWeight="bold">Structured Product Data</Text>
              {([
                { label: 'Product type', desc: 'Set a product type in the product editor → Organization → Product type. Use a clear category such as "T-Shirts" or "Running Shoes".' },
                { label: 'Variant options', desc: 'Add at least one variant option (e.g. Size, Color) under the Variants section of the product editor.' },
                { label: '3+ tags', desc: 'Add at least 3 descriptive tags in the product editor → Tags field. Examples: "outdoor", "waterproof", "unisex".' },
              ] as { label: string; desc: string }[]).map(({ label, desc }) => (
                <BlockStack key={label} gap="050">
                  <Text as="span" variant="bodySm" fontWeight="semibold">{label}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{desc}</Text>
                </BlockStack>
              ))}
            </BlockStack>
            <Divider />
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm" fontWeight="bold">Media Richness</Text>
              {([
                { label: '2+ images', desc: 'Upload at least 2 product images. Multiple angles and lifestyle shots give AI platforms more context to recommend your product.' },
                { label: 'Alt text', desc: 'Click each image in the product editor → "Edit alt text" → add a short descriptive sentence (e.g. "Blue running shoe side profile").' },
                { label: 'Sale price', desc: 'Set a "Compare at price" higher than the current price on any variant to signal a discount. This appears in the product editor under each variant\'s pricing.' },
                { label: 'Q&A / FAQ', desc: 'Install a Shopify FAQ app (e.g. HelpCenter, Easy FAQ, Globo FAQ, Avada FAQ) and add product-level questions. Zelium detects Q&A via product metafields whose key or namespace contains "faq", "question", "accordion", or "helpdesk".' },
              ] as { label: string; desc: string }[]).map(({ label, desc }) => (
                <BlockStack key={label} gap="050">
                  <Text as="span" variant="bodySm" fontWeight="semibold">{label}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{desc}</Text>
                </BlockStack>
              ))}
            </BlockStack>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* ── Gemini Score — How to fix modal ── */}
      <Modal
        open={ucpHowToFixOpen}
        onClose={() => setUcpHowToFixOpen(false)}
        title="How to fix your Gemini Score"
      >
        <Modal.Section>
          <BlockStack gap="400">
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm" fontWeight="bold">Protocol Required Fields</Text>
              {([
                { label: 'Description', desc: 'Add a product description in the product editor. This is a core requirement for Gemini to understand and index the product.' },
                { label: 'Product image', desc: 'Upload at least one product image. Gemini\'s UCP catalog requires at least one image to present the product.' },
                { label: 'In stock / available', desc: 'Ensure at least one variant is available. Check inventory settings and confirm stock levels are above zero or "Continue selling when out of stock" is enabled.' },
                { label: 'Variant options', desc: 'Add at least one variant option (e.g. Size, Color) in the Variants section. This enables Gemini\'s product matrix / option filtering.' },
              ] as { label: string; desc: string }[]).map(({ label, desc }) => (
                <BlockStack key={label} gap="050">
                  <Text as="span" variant="bodySm" fontWeight="semibold">{label}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{desc}</Text>
                </BlockStack>
              ))}
            </BlockStack>
            <Divider />
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm" fontWeight="bold">Intent &amp; Context Match</Text>
              {([
                { label: 'Description ≥ 200 chars', desc: 'Expand your description to at least 200 characters. Gemini\'s conversational search uses rich text to match buyer intent — a detailed description improves relevance.' },
                { label: '3+ tags', desc: 'Add at least 3 descriptive tags in the product editor → Tags field. Tags help Gemini match products to conversational queries.' },
              ] as { label: string; desc: string }[]).map(({ label, desc }) => (
                <BlockStack key={label} gap="050">
                  <Text as="span" variant="bodySm" fontWeight="semibold">{label}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{desc}</Text>
                </BlockStack>
              ))}
            </BlockStack>
            <Divider />
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm" fontWeight="bold">Trust &amp; Value Signals</Text>
              {([
                { label: 'Reviews / ratings', desc: 'Install a review app (e.g. Judge.me, Yotpo, Stamped.io) to collect and display product ratings. Gemini uses ratings to surface products in "best rated" queries. Detected via product metafields with a key or namespace containing "rating", "review", or a known app prefix.' },
                { label: 'Q&A / FAQ', desc: 'Install a Shopify FAQ app (e.g. HelpCenter, Easy FAQ, Globo FAQ, Avada FAQ) and add product-level questions. Zelium detects Q&A via product metafields whose key or namespace contains "faq", "question", "accordion", or "helpdesk".' },
                { label: 'Sale / compare price', desc: 'Set a "Compare at price" on any variant. Gemini uses this signal for price-filtered and deal-based queries (e.g. "on sale running shoes").' },
              ] as { label: string; desc: string }[]).map(({ label, desc }) => (
                <BlockStack key={label} gap="050">
                  <Text as="span" variant="bodySm" fontWeight="semibold">{label}</Text>
                  <Text as="p" variant="bodySm" tone="subdued">{desc}</Text>
                </BlockStack>
              ))}
            </BlockStack>
          </BlockStack>
        </Modal.Section>
      </Modal>

    </Page>
    </PageEntrance>
  );
}
